<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Check, X, ShieldCheck } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

const captures = computed(() => [
  t("landing.privacy.capture.0"),
  t("landing.privacy.capture.1"),
  t("landing.privacy.capture.2"),
  t("landing.privacy.capture.3"),
  t("landing.privacy.capture.4"),
]);

const notCaptures = computed(() => [
  t("landing.privacy.not_capture.0"),
  t("landing.privacy.not_capture.1"),
  t("landing.privacy.not_capture.2"),
  t("landing.privacy.not_capture.3"),
  t("landing.privacy.not_capture.4"),
]);
</script>

<template>
  <section class="py-24 bg-background section-halo">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-14 max-w-2xl mx-auto">
        <!-- Premium chip: gradient + inner highlight + pulsing dot.
             Replaces the flat pastel pill that all AI templates use. -->
        <div class="chip-premium mb-4">
          <span class="chip-premium-dot" />
          <ShieldCheck class="w-3.5 h-3.5" />
          <span>{{ t("landing.privacy.eyebrow") }}</span>
        </div>
        <!-- Title with typographic POV: emphasized word in italic cyan
             (the POSITIVE concept), strikethrough word in destructive
             (the NEGATED concept). The headline literally enacts what
             the section claims. -->
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">{{ t("landing.privacy.title_pre") }}<span class="word-emph text-accent">{{ t("landing.privacy.title_emph") }}</span>{{ t("landing.privacy.title_mid") }}<span class="word-strike text-destructive">{{ t("landing.privacy.title_strike") }}</span></h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.privacy.subtitle") }}
        </p>
      </AnimatedSection>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
        <!-- What WE capture -->
        <AnimatedSection animation="fade-right">
          <div class="relative h-full p-7 rounded-xl card-elevated-premium">
            <!-- File-tab corner label sticking up from the card edge.
                 Tiny detail that says "designed", not "templated". -->
            <span class="corner-tab">{{ t("landing.privacy.tab_yes") }}</span>

            <div class="flex items-center gap-3 mb-6 pb-5 border-b border-border">
              <!-- Stamp icon: diagonal gradient + inner highlight/shadow + halo.
                   Replaces the flat pastel square-with-icon. -->
              <div class="icon-stamp">
                <Check class="w-5 h-5" />
              </div>
              <h3 class="text-lg font-bold text-foreground">{{ t("landing.privacy.capture_heading_pre") }}<span class="word-emph text-accent">{{ t("landing.privacy.capture_heading_emph") }}</span>{{ t("landing.privacy.capture_heading_post") }}</h3>
            </div>
            <ul class="space-y-3">
              <li
                v-for="(item, i) in captures"
                :key="i"
                class="flex items-start gap-3 text-sm text-foreground"
              >
                <Check class="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </AnimatedSection>

        <!-- What WE DON'T capture -->
        <AnimatedSection animation="fade-left" :delay="120">
          <div class="relative h-full p-7 rounded-xl card-elevated-premium">
            <span class="corner-tab corner-tab-rose">{{ t("landing.privacy.tab_no") }}</span>

            <div class="flex items-center gap-3 mb-6 pb-5 border-b border-border">
              <div class="icon-stamp icon-stamp-rose">
                <X class="w-5 h-5" />
              </div>
              <h3 class="text-lg font-bold text-foreground">{{ t("landing.privacy.not_capture_heading_pre") }}<span class="word-emph text-destructive">{{ t("landing.privacy.not_capture_heading_emph") }}</span>{{ t("landing.privacy.not_capture_heading_post") }}</h3>
            </div>
            <ul class="space-y-3">
              <li
                v-for="(item, i) in notCaptures"
                :key="i"
                class="flex items-start gap-3 text-sm text-foreground"
              >
                <X class="w-4 h-4 mt-0.5 text-destructive flex-shrink-0" />
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </AnimatedSection>
      </div>

      <AnimatedSection animation="fade-up" :delay="200" class="mt-10 max-w-3xl mx-auto text-center">
        <p class="text-sm text-muted-foreground">
          {{ t("landing.privacy.footnote") }}
        </p>
      </AnimatedSection>
    </div>
  </section>
</template>
